/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { getSDGShortTitle, isHidden, sdgMap } from '../constants/sdg-data';
import { frameworks, Standards, standards, getGroup } from '@g17eco/core';
import {
  Company,
  Scope,
  ScopeGroups,
  SurveyActionData,
  SurveyGroupsData,
  SurveyModelMinimalUtrv
} from '../model/surveyData';
import UniversalTracker from '../model/UniversalTracker';
import {
  getQuestionName,
  getQuestionShortPrefix
} from '../utils/universalTracker';
import { PERMISSION_GROUPS } from '../utils/permission-groups';
import {
  filterFramework,
  filterMateriality,
  filterSdg,
  filterStandard,
  filterMetricGroups,
  filterStandardAndFramework,
} from '../components/survey/utils/filters';
import { Breadcrumb, ScopeQuestion, ScopeQuestionOptionalValue } from '../types/surveyScope';
import { ViewMap, ViewValues } from '../components/survey-overview-sidebar/viewOptions';
import { getScopeInfoByMap } from '../components/survey-scope/scopeSelection';
import { naturalSort } from '../utils';
import {
  ScopeQuestionGroup,
  ScopeQuestionGroupOptionalValue,
  ScopeGroup,
  ScopeCategoryGroups,
  QuestionList, BaseQuestionGroup
} from '../types/survey';
import { RootState } from '../reducers';
import { MetricGroup } from '../types/metricGroup';
import { sortItemsByTypeCode } from '../utils/sort';
import { Materiality } from '../types/initiative';
import { convertSurveyMaterialityToUtrCodeMap, getSurveyGroups, getSurveyQuestionProgress } from '../utils/survey';
import { InitiativeUniversalTracker } from '../types/initiativeUniversalTracker';

interface TagScopeGroup<T extends ScopeQuestionOptionalValue = ScopeQuestion> {
  [key: keyof Standards]: ScopeGroup<T>
}


const findUTrvByUtrId = (survey: Pick<SurveyGroupsData, 'fragmentUniversalTrackerValues'>, utrId: string, initiativeId: string) => {
  if (!survey.fragmentUniversalTrackerValues) {
    return;
  }

  const find = (utrv: Pick<SurveyModelMinimalUtrv, 'universalTrackerId' | 'initiativeId'>) => utrv.universalTrackerId === utrId && utrv.initiativeId === initiativeId;
  return survey.fragmentUniversalTrackerValues.find(find);
};

const createQuestionOptionalValue = (
  survey: SurveyGroupsData,
  universalTracker: UniversalTracker,
  initiativeId: string
): ScopeQuestionOptionalValue => ({
  utrv: findUTrvByUtrId(survey, universalTracker.getId(), initiativeId),
  universalTracker: universalTracker,
  frameworkCode: universalTracker.getFrameworkCode(),
  valueType: universalTracker.getValueType(),
  valueValidation: universalTracker.getValueValidation(),
  shortPrefix: getQuestionShortPrefix(universalTracker),
  name: getQuestionName(universalTracker),
});

export const createScopeQuestionGroupOptionalValue = (survey: SurveyGroupsData) => {
  const questionGroupList: ScopeQuestionGroupOptionalValue[] = [];
  survey.questionGroups.forEach((questionGroup) => {
    const questionList: ScopeQuestionOptionalValue[] = [];
    questionGroup.questions.forEach(({ utr }) => {
      const universalTracker = new UniversalTracker(utr);
      questionList.push(
        createQuestionOptionalValue(survey, universalTracker, survey.initiativeId)
      );
    });

    if (questionGroup.hasOwnProperty('groupData')) { // Custom Metric groups. Not reliable, but OK for now
      questionList.sort((a, b) => naturalSort(a.name, b.name));
    }

    questionGroupList.push({
      groupName: questionGroup.groupName,
      groupData: questionGroup.groupData,
      list: questionList
    });
  });
  return questionGroupList;
};

const createQuestion = (
  survey: Pick<SurveyActionData, 'fragmentUniversalTrackerValues'>,
  universalTracker: UniversalTracker,
  initiativeId: string,
): ScopeQuestion | undefined => {
  const utrv = findUTrvByUtrId(survey, universalTracker.getId(), initiativeId);
  if (!utrv) {
    return;
  }

  const questionName = getQuestionName(universalTracker);
  return {
    utrv: utrv,
    universalTracker: universalTracker,
    frameworkCode: universalTracker.getFrameworkCode(),
    valueType: universalTracker.getValueType(),
    valueValidation: universalTracker.getValueValidation(),
    shortPrefix: getQuestionShortPrefix(universalTracker),
    name: questionName,
    sortTitle: questionName
  };
};

export const createScopeQuestionGroup = (
  survey: Pick<SurveyActionData, 'questionGroups' | 'initiativeId' | 'fragmentUniversalTrackerValues'>
): ScopeQuestionGroup[] => {
  const questionGroupList: ScopeQuestionGroup[] = [];
  survey.questionGroups.forEach((questionGroup) => {
    const questionList: ScopeQuestion[] = [];
    questionGroup.questions.forEach(({ utr }) => {
      const universalTracker = new UniversalTracker(utr);
      const question = createQuestion(survey, universalTracker, survey.initiativeId);
      if (!question) {
        return;
      }
      questionList.push(question);
    });

    if (questionGroup.hasOwnProperty('groupData')) { // Custom Metric groups. Not reliable, but OK for now
      questionList.sort((a, b) => naturalSort(a.name, b.name));
    }

    questionGroupList.push({
      groupName: questionGroup.groupName,
      groupData: questionGroup.groupData,
      list: questionList
    });
  });
  return questionGroupList;
};

const getSurveyGroupsByBlueprint = createSelector(
  [
    (state: RootState) => state.survey,
  ],
  (surveyState) => {
    if (!surveyState.loaded) {
      return [];
    }
    return createScopeQuestionGroup(surveyState.data);
  }
);


export function convertToQuestionMapByCode(scopeGroups: ScopeQuestionGroup[]) {
  const questionMap = new Map<string, ScopeQuestion>();
  scopeGroups.forEach(questionGroup => {
    questionGroup.list.forEach(question => {
      questionMap.set(question.universalTracker.getCode(), question);
    });
  });
  return questionMap;
}

function convertToQuestionMapByUtrvId(scopeGroups: ScopeQuestionGroup[]) {
  const questionMap = new Map<string, ScopeQuestion>();
  if (!scopeGroups) {
    return questionMap;
  }
  scopeGroups.forEach(questionGroup => {
    questionGroup.list.forEach(question => {
      questionMap.set(question.utrv._id, question);
    });
  });
  return questionMap;
}

function convertToQuestionMapByUtrId(scopeGroups: ScopeQuestionGroup[]) {
  const questionMap = new Map<string, ScopeQuestion>();
  if (!scopeGroups) {
    return questionMap;
  }
  scopeGroups.forEach(questionGroup => {
    questionGroup.list.forEach(question => {
      questionMap.set(question.universalTracker.getId(), question);
    });
  });
  return questionMap;
}

export const convertToQuestionList = (scopeGroups: ScopeQuestionGroup[]) => {
  const questionMap = convertToQuestionMapByUtrvId(scopeGroups);
  return Array.from(questionMap, ([k, v]) => v);
}

export const getQuestionProgress = createSelector(
  [getSurveyGroupsByBlueprint],
  (questionGroupList) => {
    return getSurveyQuestionProgress(questionGroupList);
  }
)

export function initializeScopeGroupStandards<T extends ScopeQuestionOptionalValue = ScopeQuestion>() {
  const tags: TagScopeGroup<T> = {};
  for (const [tag, standard] of Object.entries(standards)) {
    tags[tag] = {
      group: {
        title: standard.name,
        subtitle: standard.subtitle,
        description: standard.description ?? '',
        src: standard.src,
        link: standard.link
      },
      list: []
    };
  }
  return tags;
}

export function initializeScopeGroupSDG<T extends ScopeQuestionOptionalValue = ScopeQuestion>() {
  const tags: TagScopeGroup<T> = {};
  sdgMap.forEach(goal => {
    const shortTitle = getSDGShortTitle(goal.code);
    const tag = goal.code;
    tags[tag] = {
      group: {
        title: `Goal ${goal.code} ${shortTitle}`,
        subtitle: goal.shortName,
      },
      list: []
    };
  });
  return tags;
}

export function initializeScopeGroupFrameworkSubGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>() {
  const tags: TagScopeGroup<T> = {};
  Object.keys(frameworks).forEach((code) => {
    const frameworkSubtypes = frameworks[code];
    return frameworkSubtypes.subgroups?.forEach((framework) => {
      const tag = framework.code;

      tags[tag] = {
        group: {
          title: framework.name,
          subtitle: framework.subtitle,
          description: framework.description,
          src: framework.src,
          colour: framework.colour
        },
        list: []
      }
    });
  });
  return tags;
}

export function initializeScopeGroupFrameworks<T extends ScopeQuestionOptionalValue = ScopeQuestion>() {
  const tags: TagScopeGroup<T> = {};
  Object.keys(frameworks).forEach((code) => {
    const framework = frameworks[code];
    tags[code] = {
      group: {
        title: framework.name,
        subtitle: framework.subtitle,
        description: framework.description,
        src: framework.src,
        colour: framework.colour
      },
      list: []
    }
  });
  return tags;
}

export function initializeScopeGroupCustomMetrics<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData) {
  const surveyInitiativeId = surveyData.initiativeId;

  const getGroupTitle = (metricGroup: Pick<MetricGroup, 'groupName' | 'initiative' | 'initiativeId'>) => {
    if (surveyInitiativeId === metricGroup.initiativeId || !metricGroup.initiative) {
      return metricGroup.groupName;
    }
    return `${metricGroup.initiative.name} Assigned Metric: ${metricGroup.groupName}`;
  };

  const tags: TagScopeGroup<T> = {};
  surveyData.customMetricGroups.forEach((metricGroup: Pick<MetricGroup, 'groupName' | 'initiative' | 'initiativeId' | '_id' | 'groupData'>) => {
    const metricGroupIcon = metricGroup.groupData?.icon ?? 'fa-file-circle-question';
    const metricGroupColour = metricGroup.groupData?.colour ?? '#e6e8ed';
    const tag = metricGroup._id;

    tags[tag] = {
      group: {
        title: getGroupTitle(metricGroup),
        iconClassName: `fal ${metricGroupIcon}`,
        colour: metricGroupColour,
        link: metricGroup.groupData?.link,
        preferredAltCodes: metricGroup.groupData?.preferredAltCodes,
      },
      list: []
    };
  });
  return tags;
}

function getSubGroupsByScopeKey<T extends ScopeQuestionOptionalValue = ScopeQuestion>(scopeKey: ScopeGroups, scopeTag: string): QuestionList<T>[] | undefined {
  switch (scopeKey) {
    case ScopeGroups.Standards:
    case ScopeGroups.Frameworks:
      const subgroups = getGroup('standards-and-frameworks', scopeTag)?.subgroups;
      return subgroups?.map(sb => ({
        code: sb.code,
        name: sb.name,
        list: [],
      }));
    default:
      return;
  }
}

function getQuestionsBySubGroup<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData,
  questions: T[],
  scopeKey: ScopeGroups,
  scopeTag: string
) {
  const subGroups = getSubGroupsByScopeKey<T>(scopeKey, scopeTag);
  if (!subGroups) {
    return;
  }

  const uniqueQuestions = new Set();

  const breadcrumbs: Breadcrumb[] = [
    { cardCategory: scopeTag, cardGroup: scopeKey.toString() as ViewValues, title: '' },
  ];
  const isSdgTag = scopeTag === ScopeGroups.Sdg;
  subGroups.forEach((subGroup) => {
    if (isSdgTag && isHidden(subGroup.code)) {
      return;
    }
    const { inScope, isPartial } = getScopeInfoByMap(
      surveyData.scope,
      subGroup.code,
      scopeKey,
      breadcrumbs,
      surveyData.initiatives[0].materialityMap
    );

    if (!inScope && !isPartial) {
      return;
    }
    const filteredQuestionList = questions.filter((q) =>
      isSdgTag ? filterSdg(q, surveyData.contributions, subGroup.code) : filterStandardAndFramework(q, subGroup.code)
    );
    filteredQuestionList.forEach(q => uniqueQuestions.add(q.universalTracker.getId()));
    subGroup.list = filteredQuestionList;
  });
  const rootLevelQuestions = questions.filter(q => !uniqueQuestions.has(q.universalTracker.getId()));

  // Only if the main tag is added, we should create virtual other group,
  // to ensure typeTags that are missing are still showing up in this virtual group,
  // otherwise everything is based off subGroups explicitly selected
  const shouldCreateOtherGroup = rootLevelQuestions.length > 0 && getScopeInfoByMap(
    surveyData.scope,
    scopeTag,
    scopeKey,
    breadcrumbs,
    surveyData.initiatives[0].materialityMap
  ).inScope;

  if (shouldCreateOtherGroup) {
    subGroups.unshift({
      code: '',
      name: 'Other',
      list: rootLevelQuestions
    });
  }
  return subGroups;
}

export function scopeGroupProcessor<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData,
  questionList: T[],
  emptyScopeGroups: ScopeCategoryGroups<T>
): ScopeCategoryGroups<T> {

  const scopeGroups: ScopeCategoryGroups<T> = JSON.parse(JSON.stringify(emptyScopeGroups));
  const questionGroups = surveyData.questionGroups.filter(group => group.groupId).map(group => ({
    _id: group.groupId as string,
    universalTrackers: group.questions.map(question => question.utr._id),
  }));

  const getFilterFunction = (scopeKey: string, question: ScopeQuestionOptionalValue) => {
    switch (scopeKey) {
      case ScopeGroups.Sdg:
        return (scopeTag: string) => filterSdg(question, surveyData.contributions, scopeTag);
      case ScopeGroups.Standards:
        return (scopeTag: string) => filterStandard(question, scopeTag);
      case ScopeGroups.Frameworks:
        return (scopeTag: string) => filterFramework(question, scopeTag);
      case ScopeGroups.Materiality:
        const materialityMap = convertSurveyMaterialityToUtrCodeMap(
          surveyData.initiatives[0]?.materialityMap
        );
        return (scopeTag: string) =>
          materialityMap ?
            filterMateriality(question, scopeTag as Materiality, materialityMap, surveyData.contributions)
            :
            undefined;
      case ScopeGroups.Custom:
        return (scopeTag: string) => filterMetricGroups(question, scopeTag, questionGroups);
      default:
        return;
    }
  }

  const getSortName = (q: T) => `${q.shortPrefix} ${q.name}`.trim();
  const sortedQuestions = questionList.sort((a, b) => getSortName(a).localeCompare(getSortName(b), navigator.languages[0] || navigator.language, {
    numeric: true,
    ignorePunctuation: true
  }));

  const scopeKeys = Object.keys(scopeGroups) as ScopeGroups[];
  sortedQuestions.forEach(question => {
    scopeKeys.forEach(scopeKey => {
      const isInScope = getFilterFunction(scopeKey, question);
      const scopeTags = Object.keys(scopeGroups[scopeKey]);
      scopeTags.forEach(scopeTag => {
        if (Array.isArray(scopeGroups[scopeKey][scopeTag]?.list) && isInScope?.(scopeTag)) {
          scopeGroups[scopeKey][scopeTag].list.push(question);
        }
      });
    });
  });

  return scopeGroups;
}

function sortCustomGroupQuestions<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  questions: T[],
  preferredAltCodes: string[] | undefined
): T[] {
  if (preferredAltCodes && preferredAltCodes.length > 0) {
    return questions.sort((a, b) => {
      return naturalSort(a.universalTracker.getBestName(preferredAltCodes), b.universalTracker.getBestName(preferredAltCodes));
    });
  }
  // William thinks this is the same, but Kes is scared
  return questions.sort((a, b) => naturalSort(a.name, b.name));
}


/**
 * Create overall list based of subgroups if available
 */
function getListFromSubGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>(questions: T[], subGroups: QuestionList<T>[] | undefined): T[] {
  if (!subGroups) {
    return questions;
  }

  const combinedQuestions = subGroups.reduce((acc, subGroup) => {
    subGroup.list.forEach(q => acc.set(q.utrv?._id ?? q.universalTracker.getId(), q))
    return acc;
  }, new Map<string, T>());

  return Array.from(combinedQuestions.values());
}

export function surveyGroupsByScopeGroupsProcessor<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  scopeGroups: ScopeCategoryGroups<T>,
) {
  const surveyGroups: BaseQuestionGroup<T>[] = [];
  const materialityMap = surveyData.initiatives[0].materialityMap;
  const scopeKeys = Object.keys(scopeGroups) as ScopeGroups[];
  scopeKeys.forEach(scopeKey => {
    const scopeTags = Object.keys(scopeGroups[scopeKey]);
    const newGroups: BaseQuestionGroup<T>[] = [];
    scopeTags.forEach(scopeTag => {
      const { inScope, isPartial } = getScopeInfoByMap(surveyData.scope, scopeTag, scopeKey, [], materialityMap);
      if (!inScope && !isPartial) {
        return;
      }

      const tagGroup = scopeGroups[scopeKey][scopeTag];
      let questions = tagGroup.list;
      if (scopeKey === 'custom') {
        questions = sortCustomGroupQuestions(questions, tagGroup.group.preferredAltCodes);
      } else {
        questions = sortItemsByTypeCode(questions, scopeTag);
      }

      const alternativeCode = standards[scopeTag] ? scopeTag : undefined;
      const subGroups = getQuestionsBySubGroup(surveyData, questions, scopeKey, scopeTag);

      newGroups.push({
        groupName: tagGroup.group.title,
        groupCode: scopeTag,
        groupData: {
          colour: tagGroup.group.colour,
          preferredAltCodes: tagGroup.group.preferredAltCodes,
          alternativeCode,
        },
        alwaysVisible: scopeKey === 'custom', // Only force custom metrics
        subGroups,
        list: getListFromSubGroups(questions, subGroups),
      });
    });
    if (newGroups.length > 0) {
      if (scopeKey === 'custom') {
        newGroups.sort((a, b) => naturalSort(a.groupName, b.groupName));
      }
      surveyGroups.push({
        groupName: ViewMap[scopeKey].label ?? scopeKey,
        groupData: {
          colour: '#1a4791'
        },
        list: [],
        count: newGroups.reduce((count, g) => count + g.list.length, 0),
        alwaysVisible: true
      });
      newGroups.forEach(g => surveyGroups.push(g));
    }
  });

  return surveyGroups;
}

export function surveyGroupsByScopeSubGroupsProcessor<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData,
  scopeGroups: ScopeCategoryGroups<T>
) {
  const surveyGroups: BaseQuestionGroup<T>[] = [];
  const materialityMap = surveyData.initiatives[0].materialityMap;
  const scopeKeys = Object.keys(scopeGroups) as ScopeGroups[];
  scopeKeys.forEach(scopeKey => {
    const scopeTags = Object.keys(scopeGroups[scopeKey]);
    const newGroups: BaseQuestionGroup<T>[] = [];
    scopeTags.forEach(scopeTag => {
      const { inScope, isPartial } = getScopeInfoByMap(surveyData.scope, scopeTag, scopeKey, [], materialityMap);
      if (!inScope && !isPartial) {
        return;
      }

      const tagGroup = scopeGroups[scopeKey][scopeTag];
      let questions = tagGroup.list;
      if (scopeKey === 'custom') {
        questions = sortCustomGroupQuestions(questions, tagGroup.group.preferredAltCodes);
      }
      const alternativeCode = standards[scopeTag] ? scopeTag : undefined;
      newGroups.push({
        groupName: tagGroup.group.title,
        groupCode: scopeTag,
        groupData: {
          colour: tagGroup.group.colour,
          preferredAltCodes: tagGroup.group.preferredAltCodes,
          alternativeCode: alternativeCode ?? tagGroup.group.preferredAltCodes?.[0],
        },
        alwaysVisible: scopeKey === 'custom', // Only force custom metrics
        list: questions
      });
    });
    if (newGroups.length > 0) {
      if (scopeKey === 'custom') {
        newGroups.sort((a, b) => naturalSort(a.groupName, b.groupName));
      }
      surveyGroups.push({
        groupName: ViewMap[scopeKey].label ?? scopeKey,
        groupData: {
          colour: '#1a4791'
        },
        list: [],
        count: newGroups.reduce((count, g) => count + g.list.length, 0),
        alwaysVisible: true
      });
      newGroups.forEach(g => surveyGroups.push(g));
    }
  });

  return surveyGroups;
}

export function surveyGroupsBySDGAndScopeProcessor<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData,
  scopeGroups: ScopeCategoryGroups<T>
) {
  const surveyGroups: BaseQuestionGroup<T>[] = [];
  const materialityMap = surveyData.initiatives[0].materialityMap;
  const scope: Partial<Scope> = surveyData.scope ? { ...surveyData.scope } : {};
  scope.sdg = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17'];

  const displayedUTRVIds = new Set<string>();
  const scopeKeys = Object.keys(scopeGroups) as ScopeGroups[];
  scopeKeys.forEach(scopeKey => {
    const scopeTags = Object.keys(scopeGroups[scopeKey]);

    const newGroups: BaseQuestionGroup<T>[] = [];
    scopeTags.forEach(scopeTag => {
      const { inScope, isPartial } = getScopeInfoByMap(scope, scopeTag, scopeKey, [], materialityMap);
      if (!inScope && !isPartial) {
        return;
      }

      const tagGroup = scopeGroups[scopeKey][scopeTag];

      let questions: T[] = [];
      if (scopeKey === 'sdg') {
        // Keep track of questions added to SDG groups, but allow duplicates across sdg goals
        tagGroup.list.forEach(q => displayedUTRVIds.add(q.universalTracker.getId()));
        questions = [...tagGroup.list];
      } else {
        // For standards, don't display questions already in sdg group, but allow duplicates across standards
        questions = tagGroup.list.filter(q => !displayedUTRVIds.has(q.universalTracker.getId()));
        // Still show empty custom metric group
        if (questions.length === 0 && 'custom' !== scopeKey) {
          return;
        }
      }

      if (scopeKey === 'custom') {
        questions = sortCustomGroupQuestions(questions, tagGroup.group.preferredAltCodes);
      }

      const alternativeCode = standards[scopeTag] ? scopeTag : undefined;
      newGroups.push({
        groupName: tagGroup.group.title,
        groupData: {
          colour: tagGroup.group.colour,
          preferredAltCodes: tagGroup.group.preferredAltCodes,
          alternativeCode
        },
        alwaysVisible: ['sdg', 'custom'].includes(scopeKey), // Only force SDG and custom scope to be always visible
        list: questions
      });
    });

    if (newGroups.length > 0) {
      if (scopeKey === 'custom') {
        newGroups.sort((a, b) => naturalSort(a.groupName, b.groupName));
      }
      surveyGroups.push({
        groupName: ViewMap[scopeKey].label ?? scopeKey,
        groupData: {
          colour: '#1a4791'
        },
        list: [],
        count: newGroups.reduce((count, g) => count + g.list.length, 0),
        alwaysVisible: true
      });
      newGroups.forEach(g => surveyGroups.push(g));
    }
  });

  return surveyGroups;
}

export const getSurveyGroupsSelector = createSelector(
  [
    (state: RootState) => state.surveySettings.overviewMode,
    (state: RootState) => state.survey
  ],
  (overviewMode, surveyState) => {
    const surveyData = surveyState.loaded ? surveyState.data : undefined;
    if (!surveyData) {
      return [];
    }
    const blueprintGroups = createScopeQuestionGroup(surveyData);
    const questionList = convertToQuestionList(blueprintGroups);
    return getSurveyGroups(overviewMode, surveyData, blueprintGroups, questionList);
  }
);

export const getQuestionListMap = createSelector(
  [getSurveyGroupsByBlueprint],
  (surveyGroups) => {
    return convertToQuestionMapByCode(surveyGroups);
  }
);

export const getTemplateQuestionListMap = createSelector(
  [getSurveyGroupsByBlueprint],
  (surveyGroups) => {
    return convertToQuestionMapByCode(surveyGroups);
  }
);

export const getQuestionOptionalValueListMap = createSelector(
  [(state: RootState) => state.survey],
  (surveyState) => {
    const questionList = new Map<string, ScopeQuestionOptionalValue>();
    if (!surveyState.loaded) {
      return questionList;
    }

    const questionGroupList = createScopeQuestionGroupOptionalValue(surveyState.data);
    if (!questionGroupList) {
      return questionList;
    }

    questionGroupList.forEach(questionGroup => {
      questionGroup.list.forEach(question => {
        questionList.set(question.universalTracker.getCode(), question);
      });
    });
    return questionList;
  }
);

export const getUtrFromSurvey = ({ survey, surveyGroups, questionId, initiativeUniversalTrackers }:
  {
    survey?: SurveyGroupsData,
    surveyGroups: ScopeQuestionGroup[],
    questionId: string,
    initiativeUniversalTrackers: InitiativeUniversalTracker[]
  }): {
    utr?: UniversalTracker,
    utrv?: SurveyModelMinimalUtrv
  } => {
  if (!survey) {
    return {};
  }

  const questionList = convertToQuestionMapByUtrId(surveyGroups);

  const findData = () => {
    const utrv = survey.fragmentUniversalTrackerValues?.find((utrv) => utrv._id === questionId);
    if (!utrv) {
      return {}
    }
    const utrId = utrv.universalTrackerId;
    const utr = questionList.get(utrId)?.universalTracker;

    if (!utr) {
      return {}
    }

    overrideInitiativeUtr({ initiativeUniversalTrackers, utrId, utr });

    return {
      utrv,
      utr,
    }
  }

  const questionDetails = findData();
  if (!questionDetails) {
    return {};
  }

  return questionDetails;
}

export const getQuestionDetails = createSelector([
  (state: RootState) => state.survey,
  getSurveyGroupsSelector,
  (_: unknown, questionId: string, initiativeUniversalTrackers: InitiativeUniversalTracker[]) => ({ questionId, initiativeUniversalTrackers }),
], (survey, surveyGroups, { questionId, initiativeUniversalTrackers }) => {

  if (!survey.loaded) {
    return {};
  }

  return getUtrFromSurvey({
    survey: survey.data,
    surveyGroups,
    questionId,
    initiativeUniversalTrackers
  });
});

const overrideInitiativeUtr = ({
  initiativeUniversalTrackers,
  utrId,
  utr,
}: {
  initiativeUniversalTrackers: InitiativeUniversalTracker[],
  utrId: string,
  utr: UniversalTracker,
}) => {
  const initiativeUtr = initiativeUniversalTrackers.find((utr) => utr.universalTrackerId === utrId);
  if (initiativeUtr) {
    utr.overrideValueValidation(initiativeUtr.valueValidation);
  }
}

export const getQuestionId = (questionIds: string[], currentId: string, currentIndex: number | string, isNext = false) => {

  const numericIndex = Number(currentIndex);
  const idCount = questionIds.length;
  const lastIndex = idCount - 1

  // Index match current id,
  let index = questionIds[numericIndex] === currentId ? numericIndex : -1;
  if (index === -1) {
    for (let i = 0; i < lastIndex; i++) {
      if (questionIds[i] === currentId) {
        index = i;
        break;
      }
    }
  }

  if (index === -1) {
    const nextQuestionIndex = isNext ? numericIndex : numericIndex - 1;
    return { id: questionIds[nextQuestionIndex] ?? questionIds[0] ?? currentId, index: nextQuestionIndex };
  }

  const nextIndex = isNext ? index + 1 : index - 1;

  // Go to beginning
  if (lastIndex < nextIndex) {
    return { id: questionIds[0] ?? currentId, index: 0 };
  }

  // Go to the end
  if (nextIndex < 0) {
    return { id: questionIds[lastIndex] ?? currentId, index: lastIndex };
  }

  return { id: questionIds[nextIndex] ?? currentId, index: nextIndex };
}

export const getPermissionGroup = createSelector(
  [
    (state: RootState) => state.globalData,
  ],
  (globalData) => {
    const getGroup = (d?: Company, currentPermissionGroup?: string) => {
      const isNonFreeGroup = d?.permissionGroup && d?.permissionGroup !== PERMISSION_GROUPS.FREE;
      return isNonFreeGroup ? d?.permissionGroup : currentPermissionGroup;
    }

    const rootOrg = globalData.loaded ? globalData.data.organization : undefined
    return getGroup(rootOrg, PERMISSION_GROUPS.FREE);
  });
